import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Poll } from "../types";
import { apiService } from "../services/api";
import { authService } from "../services/auth";
import { analyticsService } from "../services/analytics";
import { PollCard } from "../components/PollCard";
import { CreatePollForm } from "../components/CreatePollForm";
import "./FeedPage.css";

interface FeedPageProps {
  showCreateForm?: boolean;
  onShowCreateFormChange?: (show: boolean) => void;
}

export const FeedPage: React.FC<FeedPageProps> = ({
  showCreateForm: externalShowCreateForm,
  onShowCreateFormChange,
}) => {
  const navigate = useNavigate();
  const { pollId } = useParams<{ pollId?: string }>();
  const userInfo = authService.getUserInfo();
  console.log("FeedPage: pollId param:", pollId);
  const [currentPoll, setCurrentPoll] = useState<Poll | null>(null);
  const [previousPolls, setPreviousPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [internalShowCreateForm, setInternalShowCreateForm] = useState(false);
  const [noMorePolls, setNoMorePolls] = useState(false);

  // Use external state if provided, otherwise use internal state
  const showCreateForm = externalShowCreateForm ?? internalShowCreateForm;
  const setShowCreateForm = onShowCreateFormChange ?? setInternalShowCreateForm;

  useEffect(() => {
    console.log("FeedPage: pollId param changed:", pollId);
    if (pollId) {
      // Only load if we don't already have this poll
      if (!currentPoll || currentPoll.pollId !== pollId) {
        loadPollById(pollId);
      }
    } else if (!currentPoll) {
      // Check if there's a saved poll ID in localStorage
      const savedPollId = localStorage.getItem('lastViewedPollId');
      if (savedPollId) {
        // Navigate to the saved poll
        navigate(`/feed/${savedPollId}`, { replace: true });
      } else {
        // Only load next poll if we don't have a saved poll
        loadNextPoll();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const loadPollById = async (id: string) => {
    setIsLoading(true);
    setError(null);
    setNoMorePolls(false);

    try {
      const poll = await apiService.getPoll(id);
      setCurrentPoll(poll);
      // Save the poll ID to localStorage
      localStorage.setItem('lastViewedPollId', id);
    } catch (err: any) {
      console.warn(`Failed to load poll by ID ${id}, clearing saved poll and loading next:`, err);
      localStorage.removeItem('lastViewedPollId');
      // Fallback to loading the next poll automatically
      await loadNextPoll();
    } finally {
      setIsLoading(false);
    }
  };

  const loadNextPoll = async () => {
    setIsLoading(true);
    setError(null);
    setNoMorePolls(false);

    try {
      const poll = await apiService.getNextPoll();
      if (poll) {
        // Save current poll to history before loading next
        if (currentPoll) {
          setPreviousPolls([...previousPolls, currentPoll]);
        }

        setCurrentPoll(poll);
        navigate(`/feed/${poll.pollId}`, { replace: true });
        // Save the poll ID to localStorage
        localStorage.setItem('lastViewedPollId', poll.pollId);
      } else {
        setNoMorePolls(true);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load poll");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePollCreated = (createdPoll: Poll) => {
    setShowCreateForm(false);
    setPreviousPolls([]);
    // Navigate to the newly created poll
    setCurrentPoll(createdPoll);
    navigate(`/feed/${createdPoll.pollId}`, { replace: true });
    localStorage.setItem('lastViewedPollId', createdPoll.pollId);
  };

  const handleVote = (updatedPoll: Poll) => {
    setCurrentPoll(updatedPoll);
  };

  const handleDelete = (deletedPollId: string) => {
    // If the deleted poll is the current poll, load next poll
    if (currentPoll?.pollId === deletedPollId) {
      setCurrentPoll(null);
      localStorage.removeItem('lastViewedPollId');
      loadNextPoll();
    }
  };

  const handleNext = async () => {
    if (!isLoading) {
      if (currentPoll && !currentPoll.userVote) {
        // Record the skip asynchronously in the background so navigation is instant
        // Only if user is authenticated
        if (userInfo) {
          apiService.skipPoll(currentPoll.pollId).catch((err) => {
            console.error("Failed to record skip in backend:", err);
          });
        }

        // Track skip in analytics
        try {
          analyticsService.trackPollSkipped(
            currentPoll.pollId,
            currentPoll.category || "General",
            userInfo?.sub || "anonymous_user"
          );
        } catch (analyticsErr) {
          console.warn("Failed to track skip in analytics:", analyticsErr);
        }
      }
      loadNextPoll();
    }
  };

  const handlePrevious = () => {
    if (previousPolls.length > 0) {
      const previous = previousPolls[previousPolls.length - 1];
      const newPreviousPolls = previousPolls.slice(0, -1);

      if (currentPoll) {
        // We're going back, so we don't add current to history
        setCurrentPoll(previous);
        setPreviousPolls(newPreviousPolls);
        navigate(`/feed/${previous.pollId}`, { replace: true });
        // Save the poll ID to localStorage
        localStorage.setItem('lastViewedPollId', previous.pollId);
      }
    }
  };

  const hasPrevious = previousPolls.length > 0;

  return (
    <div className="feed-page">
      <div className="feed-container">
        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
          <div className="shimmer-container">
            <div className="shimmer-card">
              <div className="shimmer-header">
                <div className="shimmer-avatar" />
                <div className="shimmer-meta">
                  <div className="shimmer-line short" />
                  <div className="shimmer-line xshort" />
                </div>
              </div>
              <div className="shimmer-title" />
              <div className="shimmer-options">
                <div className="shimmer-option" />
                <div className="shimmer-option" />
              </div>
            </div>
          </div>
        ) : noMorePolls ? (
          <div className="empty-state">
            <h2>You've seen all the polls!</h2>
            <p style={{ marginBottom: "16px" }}>Explore closed polls to see final results for historical polls.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/explore?tab=closed")}
            >
              Explore Closed Polls
            </button>
          </div>
        ) : !currentPoll ? (
          <div className="empty-state">
            <h2>No polls yet</h2>
            <p>Be the first to create a poll!</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              Create Your First Poll
            </button>
          </div>
        ) : (
          <div className="poll-card-container">
            <PollCard
              key={currentPoll.pollId}
              poll={currentPoll}
              onVote={handleVote}
              onDelete={handleDelete}
            />
          </div>
        )}
      </div>

      {currentPoll && !showCreateForm && (
        <div className="poll-navigation">
          <div className="poll-navigation-inner">
            <button
              className="btn btn-secondary nav-previous"
              onClick={handlePrevious}
              disabled={!hasPrevious || isLoading}
              aria-label="Previous poll"
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
              <span>Previous</span>
            </button>

            <button
              className="btn btn-primary nav-next"
              onClick={handleNext}
              disabled={isLoading || noMorePolls}
              aria-label="Next poll"
            >
              <span>
                {isLoading ? "Loading..." : (currentPoll.userVote ? "Next Poll" : (!userInfo ? "View Next" : "Skip This Poll"))}
              </span>
              {isLoading ? (
                <svg className="nav-spinner" viewBox="0 0 50 50" style={{ width: '18px', height: '18px', marginLeft: '6px' }}>
                  <circle className="nav-spinner-circle" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                </svg>
              ) : (
                <ChevronRight size={20} strokeWidth={2.5} />
              )}
            </button>
          </div>
        </div>
      )}

      <CreatePollForm
        open={showCreateForm}
        onOpenChange={setShowCreateForm}
        onPollCreated={handlePollCreated}
      />
    </div>
  );
};
